// AssetListModal - カテゴリ分けされたアセット一覧モーダル
// かざり（タイルの上に置く）と じめん（地面レベル）に分類

import { useDispatch, useSelector } from "react-redux";
import { useRef, useMemo, useState, useEffect } from "react";
import { produce } from "immer";
import { Dialog } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ForestIcon from "@mui/icons-material/Forest";
import LandscapeIcon from "@mui/icons-material/Landscape";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { AssetModel } from "common/model";
import { usePlaySound } from "common/utils";
import { FadeTransition } from "common/elements";
import assets from "features/builder/assets";
import { actions } from "features/builder/slice";

// カテゴリヘッダー
const CategoryHeader = ({
  icon,
  label,
  hint,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  color: string;
}) => (
  <div className="flex-col-el gap-1 mb-3">
    <div className={`flex-row-el items-center gap-2 ${color}`}>
      {icon}
      <span className="text-lg">{label}</span>
    </div>
    {hint && (
      <div className="flex-row-el items-center gap-1 text-sm text-gray-500 ml-1">
        <InfoOutlinedIcon style={{ fontSize: 16 }} />
        <span>{hint}</span>
      </div>
    )}
  </div>
);

// アセットグリッドアイテム
const AssetGridItem = ({
  asset,
  selected,
  size,
  onSelect,
}: {
  asset: AssetModel;
  selected: boolean;
  size: number;
  onSelect: (asset: AssetModel) => void;
}) => (
  <div
    onClick={() => onSelect(asset)}
    style={{ width: size, height: size }}
    className={`flex-col-el flex-center shrink-0 rounded-xl cursor-pointer transition-all duration-150 active:scale-95 border-2
      ${
        selected
          ? "border-blue-400 bg-blue-50 shadow-md"
          : "border-transparent bg-white/60 hover:bg-white hover:shadow-sm"
      }`}
  >
    <img
      draggable="false"
      className="w-full h-full object-contain select-none p-2"
      src={`${Constants.assetHost}/assets/models/${asset.name}.png`}
    />
  </div>
);

export const AssetListModal = () => {
  const play = usePlaySound();
  const dispatch = useDispatch();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [itemSize, setItemSize] = useState(80);
  const selectedSlotIndex = useSelector((state: RootState) => state.builder.selectedSlotIndex);
  const show = useSelector((state: RootState) => state.builder.actionMenu.assetList);
  const slot = useSelector((state: RootState) => state.builder.slot);

  // カテゴリ分類
  // かざり: decoration, gem, condition（タイルの上に置く）
  // じめん: ground, river（地面レベル layer 0）
  const decorationAssets = useMemo(() => {
    const decoration = assets.find((a) => a.name === "decoration")?.models ?? [];
    const gem = assets.find((a) => a.name === "gem")?.models ?? [];
    const condition = assets.find((a) => a.name === "condition")?.models ?? [];
    return [...decoration, ...gem, ...condition];
  }, []);

  const groundAssets = useMemo(() => {
    const ground = assets.find((a) => a.name === "ground")?.models ?? [];
    const river = assets.find((a) => a.name === "river")?.models ?? [];
    return [...ground, ...river];
  }, []);

  // レスポンシブサイズ計算
  useEffect(() => {
    const computeSize = () => {
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const minDim = Math.min(vh, vw);
      const size = Math.max(56, Math.min(88, Math.floor(minDim * 0.12)));
      setItemSize(size);
    };
    computeSize();
    window.addEventListener("resize", computeSize);
    return () => window.removeEventListener("resize", computeSize);
  }, []);

  // スロット選択初期化
  useEffect(() => {
    if (show && (selectedSlotIndex === null || selectedSlotIndex === undefined)) {
      dispatch(actions.selectSlot(0));
    }
  }, [show, selectedSlotIndex, dispatch]);

  const handleClose = () => {
    play();
    const slotAsset = slot[selectedSlotIndex ?? 0];
    if (!slotAsset) {
      dispatch(actions.selectSlot(null));
      dispatch(actions.updateActionMenu({ key: "tap", value: true, exclusive: true }));
    }
    dispatch(actions.updateActionMenu({ key: "assetList", value: false }));
  };

  const handleSelectAsset = (asset: AssetModel) => {
    play();
    const existingIndex = slot.findIndex((v) => v?.name === asset.name);
    const targetIndex = selectedSlotIndex ?? 0;

    if (existingIndex === -1) {
      // 新規追加
      const newSlot = produce(slot, (draft) => {
        draft[targetIndex] = asset;
      });
      dispatch(actions.updateSlot(newSlot));
    } else {
      // 既存を移動
      const newSlot = produce(slot, (draft) => {
        draft[existingIndex] = null;
        draft[targetIndex] = asset;
      });
      dispatch(actions.updateSlot(newSlot));
    }
    dispatch(actions.selectedAssetMesh(asset));
  };

  const isSelected = (asset: AssetModel) => slot.some((v) => v?.name === asset.name);

  return (
    <Dialog
      fullScreen
      open={show}
      maxWidth={false}
      sx={{ zIndex: 100000001 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div
        className="flex-col-el flex-center absolute inset-0 bg-black/40 w-full h-full"
        onClick={handleClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="flex-col-el relative max-w-[90vw] max-h-[85vh] rounded-2xl bg-white/95 backdrop-blur-md shadow-2xl overflow-hidden"
        >
          {/* ヘッダー */}
          <div className="flex-row-el items-center justify-between px-5 py-3 border-b border-gray-200 bg-white/80">
            <h2 className="text-xl text-gray-700">{I18n.t("MSG_BUILDER_ASSET_LIST_TITLE")}</h2>
            <button
              onClick={handleClose}
              className="flex-col-el flex-center w-10 h-10 rounded-full hover:bg-gray-100 active:scale-95 transition-all"
            >
              <CloseIcon className="text-gray-500" />
            </button>
          </div>

          {/* コンテンツ */}
          <div ref={scrollRef} className="flex-col-el gap-6 p-5 overflow-auto">
            {/* かざりカテゴリ */}
            <div className="flex-col-el">
              <CategoryHeader
                icon={<ForestIcon style={{ fontSize: 24 }} />}
                label={I18n.t("MSG_BUILDER_ASSET_CATEGORY_DECORATION")}
                color="text-green-600"
              />
              <div className="flex-row-el flex-wrap gap-3">
                {decorationAssets.map((asset, index) => (
                  <AssetGridItem
                    key={`deco_${index}`}
                    asset={asset}
                    selected={isSelected(asset)}
                    size={itemSize}
                    onSelect={handleSelectAsset}
                  />
                ))}
              </div>
            </div>

            {/* じめんカテゴリ */}
            <div className="flex-col-el">
              <CategoryHeader
                icon={<LandscapeIcon style={{ fontSize: 24 }} />}
                label={I18n.t("MSG_BUILDER_ASSET_CATEGORY_GROUND")}
                hint={I18n.t("MSG_BUILDER_ASSET_CATEGORY_GROUND_HINT")}
                color="text-amber-600"
              />
              <div className="flex-row-el flex-wrap gap-3">
                {groundAssets.map((asset, index) => (
                  <AssetGridItem
                    key={`ground_${index}`}
                    asset={asset}
                    selected={isSelected(asset)}
                    size={itemSize}
                    onSelect={handleSelectAsset}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
